import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Redirect } from './router';
import { useAuthStore } from '@/features/auth/authStore';
import { Splash } from '@/components/ui/Splash';
import { SagesseTabBar, type TabRoute } from '@/components/ui/SagesseTabBar';
import { SiteHeader } from '@/components/ui/SiteHeader';

/**
 * The route-group layouts, ported from expo-router's `_layout.tsx` files.
 *
 * `(app)/_layout` becomes <AppLayout>, `(auth)/_layout` becomes <AuthLayout>,
 * `(app)/(tabs)/_layout` becomes <TabsLayout> — same guards, same nesting, same
 * order of checks. React Router's <Outlet> plays the part expo-router's <Stack>
 * played: the slot the matched child route renders into.
 */

/** Entry route: send the user to the app or the auth flow based on session status. */
export function Index() {
  const status = useAuthStore((s) => s.status);
  if (status === 'loading') {
    return <Splash />;
  }
  return <Redirect href={status === 'authenticated' ? '/home' : '/welcome'} />;
}

/** Auth flow: if already signed in, bounce to the app. */
export function AuthLayout() {
  const status = useAuthStore((s) => s.status);
  if (status === 'authenticated') {
    return <Redirect href="/home" />;
  }
  // No site nav here: there is nowhere to navigate to until you are signed in.
  return <ScreenSlot id="main" className="auth-slot" />;
}

/**
 * Protected area: gate behind an authenticated session.
 *
 * On native this also declared the stack every detail screen pushes onto. The
 * browser keeps that stack for us — it is the history — so the declaration is
 * just the guard.
 */
export function AppLayout() {
  const status = useAuthStore((s) => s.status);
  if (status === 'loading') {
    return <Splash />;
  }
  if (status !== 'authenticated') {
    return <Redirect href="/welcome" />;
  }
  return (
    <>
      <SiteHeader />
      <ScreenSlot id="main" />
    </>
  );
}

/** The six bottom tabs. Detail screens render outside this layout, so they cover
 *  the tab bar exactly as a pushed screen does on the phone. */
export function TabsLayout() {
  const { t } = useTranslation('tabs');

  const tabs: TabRoute[] = [
    { name: 'home', path: '/home', title: t('home') },
    { name: 'match', path: '/match', title: t('match') },
    { name: 'shop', path: '/shop', title: t('shop') },
    { name: 'community', path: '/community', title: t('community') },
    { name: 'me', path: '/me', title: t('me') },
    { name: 'more', path: '/more', title: t('more') },
  ];

  return (
    <>
      <ScreenSlot />
      {/* Below --bp-site this is the navigation; above it the site header is,
          and CSS drops this whole subtree (display:contents -> none). */}
      <div className="only-compact">
        <SagesseTabBar tabs={tabs} />
      </div>
    </>
  );
}

/**
 * One screen's slot.
 *
 * Native pushes a new screen onto a stack, which resets its scroll position and
 * animates it in. The browser does neither for a client-side route change, so
 * both are done here — scroll to the top on a push (but NOT on a back, where
 * the browser restores the previous position and overriding it loses the fan's
 * place in a long list), and replay the stack's enter animation.
 */
/**
 * `id` is the skip link's target and belongs to the OUTERMOST slot on the page:
 * (app) nests (tabs), so a tab route renders two of these and only the outer
 * one is the main landmark.
 */
function ScreenSlot({ className, id }: { className?: string; id?: string }) {
  const location = useLocation();

  useEffect(() => {
    const isPop = (window.history.state as { idx?: number } | null) === null;
    if (!isPop) {
      document.querySelector('.app-shell')?.scrollTo({ top: 0 });
    }
  }, [location.pathname]);

  return (
    // tabIndex -1: focusable only as the skip link's destination, never in
    // the tab order itself.
    <div
      id={id}
      tabIndex={id ? -1 : undefined}
      className={['screen-root', 'screen-enter', className].filter(Boolean).join(' ')}
      key={location.pathname}
    >
      <Outlet />
    </div>
  );
}
